/**
 * 标准配合残差冲突归因策略。
 *
 * 文件职责：
 * 1. 判断残差失败是否具备升级为 SOLIDWORKS “过定义”的证据；
 * 2. 避免仅凭同一组件对存在其它 Mate 就误判过定义；
 * 3. 保持输入输出为 Mate Definition 纯数据，不依赖 Viewer、UI 或 Three.js 运行时对象。
 */

import {
  AssemblyMateType,
  MateConcentricMisalignmentType,
  MateEntityKind,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../../definition';

/** 普通残差失败：距离、方向或位置本身没有满足目标约束。 */
export const STANDARD_MATE_RESIDUAL_FAILURE_GENERAL = 'general';

/** Distance Mate 绝对距离满足但落到 SOLIDWORKS Flip Dimension 镜像侧。 */
export const STANDARD_MATE_RESIDUAL_FAILURE_DISTANCE_BRANCH_SIDE = 'distanceBranchSide';

/**
 * 标准配合残差失败类型。
 *
 * 设计说明：
 * SOLIDWORKS 的过定义诊断不应只由“同一组件对存在另一条 Mate”推出。
 * 对点/球-轴类 Distance，只有绝对距离已满足、但尺寸侧被其它约束强制到镜像侧时，
 * 才具备升级为 over-defined 的证据；普通距离值不满足仍然只是 unsolved。
 */
export type StandardMateResidualFailureKind =
  | typeof STANDARD_MATE_RESIDUAL_FAILURE_GENERAL
  | typeof STANDARD_MATE_RESIDUAL_FAILURE_DISTANCE_BRANCH_SIDE;

/**
 * 解析实体的残差冲突 key。
 *
 * @param entity Mate 实体引用。
 * @returns 稳定实体 key。
 */
function resolveResidualEntityKey(entity: MateEntityRef): string {
  return [
    entity.componentInstanceId,
    entity.stableRef,
    entity.entityKind,
  ].join('|');
}

/**
 * 解析 Mate 第一、第二实体所在的无方向组件对。
 *
 * @param mate Mate 定义。
 * @returns 无方向组件对 key。
 */
function resolveMatePrimaryComponentPairKey(mate: AssemblyMateDefinition): string {
  return [
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
  ].sort().join('::');
}

/**
 * 解析 Mate 的无方向实体对 key。
 *
 * @param mate Mate 定义。
 * @returns 无方向实体对 key。
 */
function resolveMateEntityPairKey(mate: AssemblyMateDefinition): string {
  return [
    resolveResidualEntityKey(mate.firstEntity),
    resolveResidualEntityKey(mate.secondEntity),
  ].sort().join('::');
}

/**
 * 判断两条 Mate 是否约束同一组主组件。
 *
 * @param first 第一条 Mate。
 * @param second 第二条 Mate。
 * @returns 是否共享同一组主组件。
 */
function doMatesSharePrimaryComponentPair(
  first: AssemblyMateDefinition,
  second: AssemblyMateDefinition,
): boolean {
  return resolveMatePrimaryComponentPairKey(first) === resolveMatePrimaryComponentPairKey(second);
}

/**
 * 判断两条 Mate 是否引用同一对拓扑实体。
 *
 * @param first 第一条 Mate。
 * @param second 第二条 Mate。
 * @returns 是否共享同一对拓扑实体。
 */
function doMatesShareEntityPair(
  first: AssemblyMateDefinition,
  second: AssemblyMateDefinition,
): boolean {
  return resolveMateEntityPairKey(first) === resolveMateEntityPairKey(second);
}

/**
 * 判断 Mate 是否已经锁住组件对的整体位姿或绕轴姿态。
 *
 * @param mate Mate 定义。
 * @returns 是否属于强姿态锁定约束。
 */
function isRigidPoseLockingMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Lock
    || (mate.mateType === AssemblyMateType.Concentric && mate.lockRotation === true);
}

/**
 * 判断是否为 SOLIDWORKS 错位同轴心 Mate。
 *
 * @param mate Mate 定义。
 * @returns 是否为已链接的错位同轴心 Mate。
 */
function isMisalignedConcentricMate(mate: AssemblyMateDefinition): boolean {
  return mate.mateType === AssemblyMateType.Concentric
    && (
      mate.concentricMisalignment?.type === MateConcentricMisalignmentType.AlignLinkedMate
      || mate.concentricMisalignment?.type === MateConcentricMisalignmentType.AlignThisMate
      || mate.concentricMisalignment?.type === MateConcentricMisalignmentType.Symmetric
    )
    && typeof mate.concentricMisalignment.linkedMateId === 'string'
    && mate.concentricMisalignment.linkedMateId.length > 0;
}

/**
 * 判断实体是否可作为 Distance 镜像侧校验中的点状实体。
 *
 * @param entity Mate 实体引用。
 * @returns 是否可按点状距离处理。
 */
function isPointLikeDistanceEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Point
    || entity.entityKind === MateEntityKind.Origin
    || entity.entityKind === MateEntityKind.CoordinateSystem
    || entity.entityKind === MateEntityKind.Sphere;
}

/**
 * 判断实体是否可作为 Distance 镜像侧校验中的轴线实体。
 *
 * @param entity Mate 实体引用。
 * @returns 是否可按轴线距离处理。
 */
function isAxisLikeDistanceEntity(entity: MateEntityRef): boolean {
  return entity.entityKind === MateEntityKind.Line
    || entity.entityKind === MateEntityKind.ReferenceAxis
    || entity.entityKind === MateEntityKind.CircularEdge
    || entity.entityKind === MateEntityKind.Cylinder
    || entity.entityKind === MateEntityKind.Cone;
}

/**
 * 判断 Distance Mate 是否存在 SOLIDWORKS Flip Dimension 语义中的镜像尺寸侧。
 *
 * @param mate Mate 定义。
 * @returns 是否属于镜像侧敏感的 Distance。
 */
function isBranchSensitiveDistanceMate(mate: AssemblyMateDefinition): boolean {
  if (mate.mateType !== AssemblyMateType.Distance) {
    return false;
  }

  const firstPointLike = isPointLikeDistanceEntity(mate.firstEntity);
  const secondPointLike = isPointLikeDistanceEntity(mate.secondEntity);
  const firstAxisLike = isAxisLikeDistanceEntity(mate.firstEntity);
  const secondAxisLike = isAxisLikeDistanceEntity(mate.secondEntity);

  /**
   * 点-点、点-轴、球-轴等组合存在“绝对距离满足但落在创建侧镜像位置”的情况；
   * 平面类距离由法向/圆形距离模式处理，不应仅凭同组件对已有 Mate 就判 overDefined。
   */
  return (firstPointLike && secondPointLike)
    || (firstPointLike && secondAxisLike)
    || (firstAxisLike && secondPointLike);
}

/**
 * 判断两条 Mate 是否具备残差冲突证据。
 *
 * 设计说明：
 * SOLIDWORKS 的过定义需要来自可定位的重复自由度或真实冲突。普通残差失败时，
 * “同一组件对有另一条 Mate”只能说明求解图更复杂，不能直接证明自由度重叠。
 * 这里按三类证据收口：
 * 1. 同一实体对：视为同一局部自由度集合，残差失败可升级为过定义；
 * 2. Lock / 同轴心锁定旋转：它们锁住组件对整体或绕轴姿态，和同组件对其它 Mate 冲突时可升级；
 * 3. 点/球到点/轴类 Distance 的镜像侧被其它同组件对 Mate 强制时，可升级为真实尺寸侧冲突；
 * 4. Distance / Angle 的冗余由 StandardMateRedundantConstraintValidator 按实体对和尺寸槽位处理。
 *
 * @param first 第一条 Mate。
 * @param second 第二条 Mate。
 * @returns 是否存在足够证据把残差失败归因为过定义。
 */
function doMatesHaveResidualConflictEvidence(
  first: AssemblyMateDefinition,
  second: AssemblyMateDefinition,
  failureKind: StandardMateResidualFailureKind,
): boolean {
  if (
    isMisalignedConcentricMate(first)
    && first.concentricMisalignment?.linkedMateId === second.mateId
  ) {
    return true;
  }
  if (
    isMisalignedConcentricMate(second)
    && second.concentricMisalignment?.linkedMateId === first.mateId
  ) {
    return true;
  }

  if (isRigidPoseLockingMate(first) || isRigidPoseLockingMate(second)) {
    return doMatesSharePrimaryComponentPair(first, second);
  }

  if (
    isBranchSensitiveDistanceMate(first)
    && failureKind === STANDARD_MATE_RESIDUAL_FAILURE_DISTANCE_BRANCH_SIDE
  ) {
    return doMatesSharePrimaryComponentPair(first, second);
  }

  return doMatesShareEntityPair(first, second);
}

/**
 * 判断残差失败是否存在可归因的同伴 Mate。
 *
 * @param input 判断输入。
 * @returns 是否应把残差失败归类为真实过定义。
 */
export function hasResidualConflictPeer(input: {
  /** 当前残差失败的 Mate。 */
  readonly mate: AssemblyMateDefinition;
  /** 当前残差失败原因。 */
  readonly failureKind: StandardMateResidualFailureKind;
  /** 本轮参与校验的全部 Mate。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** PropertyManager 中尚未提交的临时 Mate ID。 */
  readonly transientMateIds: ReadonlySet<string>;
}): boolean {
  return input.mateDefinitions.some((candidate) => (
    candidate.mateId !== input.mate.mateId
    && !candidate.suppressed
    && !input.transientMateIds.has(candidate.mateId)
    && doMatesHaveResidualConflictEvidence(input.mate, candidate, input.failureKind)
  ));
}
